// src/domain/models/task/taskModel.js
import dayjs from 'dayjs';
import { validatePriority } from '@/domain/models/taskLine/taskLineHelpers.js';
import { parseTaskLine } from '@/domain/models/taskLine/taskLineParser.js';
import { REGEX_COMPLETION_MARK } from '../taskLine/taskLineHelpers.js';
import {
  setPrivateData,
  getPrivateData,
  defineInstanceReference
} from '@/domain/models/privateMemory.js';
import { createTaskProxy } from './taskProxy.js';
import {
  buildStringRepresentation,
  buildCoreSegment,
  buildExtendedAttributesBlock
} from './taskFormatter.js';

/**
 * Represents a Task with core and extended attributes.
 * - Core attributes have predefined getters and setters.
 * - Extended attributes are managed dynamically through a Proxy.
 *
 * A Task instance encapsulates the original definition representation
 * and its parsed components, allowing for robust manipulation and
 * representation logic.
 *
 * @class
 */
export function Task(definition) {
  if (typeof definition !== 'string' || !definition.trim()) {
    throw new Error('A non-empty string is required to create a Task.');
  }
  defineInstanceReference(this);

  //Augment the instance with the taskProxy.
  const self = createTaskProxy(this);
  // Parse the input definition and initialize private storage
  const parsed = parseTaskLine(definition);
  setPrivateData(this, { parsed, originalLine: definition.trim() });
  // Return a Proxy-wrapped instance to handle dynamic attributes
  return self;
}

// [ Core Attribute Getters and Setters ]

/**
 * Task description getter and setter.
 * The description is a mandatory non-empty string.
 */
Object.defineProperty(Task.prototype, 'description', {
  get() {
    return getPrivateData(this).parsed.description;
  },
  set(value) {
    if (typeof value !== 'string' || !value.trim()) {
      throw new Error('description must be a non-empty string.');
    }
    getPrivateData(this).parsed.description = value;
  },
  enumerable: true
});

/**
 * Task priority getter and setter.
 * Uses a parser to validate and standardize the input.
 */
Object.defineProperty(Task.prototype, 'priority', {
  get() {
    return getPrivateData(this).parsed.priority;
  },
  set(value) {
    if (!validatePriority(value)) return;
    getPrivateData(this).parsed.priority = value;
  },
  enumerable: true
});

/**
 * Task tags getter and setter.
 * Tags are stored as an array of strings.
 */
Object.defineProperty(Task.prototype, 'tags', {
  get() {
    return getPrivateData(this).parsed.tags;
  },
  set(value) {
    if (!Array.isArray(value)) {
      throw new Error('tags must be an array of strings.');
    }
    getPrivateData(this).parsed.tags = value.map(String);
  },
  enumerable: true
});

/**
 * Task completion mark getter and setter.
 * The completion mark must be a single non-alphanumeric character or null.
 */
Object.defineProperty(Task.prototype, 'completionMark', {
  get() {
    return getPrivateData(this).parsed.completionMark || null;
  },
  set(value) {
    if (value !== null && !REGEX_COMPLETION_MARK.test(value)) {
      throw new Error(
        'completionMark must be a single non-alphanumeric character or null.'
      );
    }
    getPrivateData(this).parsed.completionMark = value;
  },
  enumerable: true
});

// [Computed only get values]

/**
 * Task is completed
 * Computed value based on the prescence of a CompletionMark or completed_at
 * attribute.
 */
Object.defineProperty(Task.prototype, 'isCompleted', {
  get() {
    return !!this.completed_at || !!this.completionMark;
  }
});

// [ Task Methods ]

/**
 * Marks the Task as completed by setting the `completed_at` attribute
 * to the current ISO datetime string.
 */
Task.prototype.markAsCompleted = function () {
  if (this.completed_at === undefined) {
    this.completed_at = dayjs().toISOString(); // Managed by Proxy
  }
};

/**
 * Updates the Task's state using a new definition string representation.
 * Parses the definition and overwrites the stored data.
 *
 * @param {string} definition - New definition string to parse and update from.
 */
Task.prototype.updateFromLine = function (definition) {
  if (typeof definition !== 'string' || !definition.trim()) {
    throw new Error('A non-empty string is required to update the Task.');
  }
  const parsed = parseTaskLine(definition);
  getPrivateData(this).parsed = parsed;
  getPrivateData(this).originalLine = definition.trim();
};

/**
 * Returns the original definition string as it was at the time of creation.
 *
 * @returns {string} - Original definition representation.
 */
Task.prototype.getOriginalLine = function () {
  return getPrivateData(this).originalLine;
};

/**
 * Constructs a string representation of the Task.
 * Delegates formatting to a utility function.
 *
 * @returns {string} - Current definition representation.
 */
Task.prototype.toString = function () {
  const { parsed } = getPrivateData(this);
  return buildStringRepresentation(parsed);
};

/**
 * Constructs the core segment of the Task's definition representation.
 *
 * @returns {string} - Core segment string.
 */
Task.prototype.getCoreSegmentString = function () {
  const { parsed } = getPrivateData(this);
  return buildCoreSegment(parsed);
};

/**
 * Constructs the extended attributes block of the Task's representation.
 *
 * @returns {string|null} - Extended attributes string or null if none exist.
 */
Task.prototype.getExtendedAttributesString = function () {
  const { parsed } = getPrivateData(this);
  return buildExtendedAttributesBlock(parsed);
};

// [ Factory Functions ]

/**
 * Factory function to create a new Task instance from a definition.
 *
 * @param {string} definition - Line string to parse and initialize the Task.
 * @returns {Task} - A new Task instance.
 */
export const createTaskFromLine = (definition) => new Task(definition);

/**
 * Creates a Task instance from a raw task object.
 * @param {Object} taskData - The raw task data from the API.
 * @returns {Task} - The created Task instance.
 */
export const createTaskFromObject = (taskObj) => {
  const task = createTaskFromLine(taskObj.definition);
  // Assign all other properties generically
  Object.keys(taskObj).forEach((key) => {
    if (key === 'definition' || !taskObj[key]) return;
    task[key] = taskObj[key];
  });
  return task;
};
